<template>
  <div v-if="isLoggedIn">
    <h4>YOU CANNOT ACCESS THIS PAGE WHILE LOGGED IN. PLEASE LOG OUT AND RE-OPEN THE LINK TO ACCESS.</h4>
  </div>
  <div v-else class="content">
    <div class="reset-wrapper">
      <div class="reset">
        <PublicBrandHeader />
        <h3>Nurse Verification</h3>

        <div class="row mb-2 gx-2">
          <div class="col-12 form-group form-floating mb-2 mb-lg-0">
            <input type="text" class="form-control" :value="accountTypeLabel" disabled />
            <label class="profile_label">Account Type:</label>
          </div>
        </div>

        <p class="reset__introduction">
          Nursing accounts are no longer verified here. Your invitation e-mail comes
          from the pacmny sign-in service, and that is where you choose your username
          and password — this application never handles either.
        </p>
        <p class="reset__introduction">
          Your licence details, insurance documents and Nursys enrolment are not
          collected on this page, so there is nothing further to fill in here.
        </p>
        <p class="reset__introduction">
          If your invitation has expired, please ask your organization to send a new one.
        </p>

        <div class="button-group">
          <button
            type="button"
            class="btn btn--login"
            id="sbmtuserbtn"
            :disabled="redirecting"
            @click="continueToSignIn"
          >
            {{ redirecting ? 'Taking you there...' : 'Continue to sign in' }}
          </button>
          <a :href="forgotPasswordUrl" class="form__link">Can't find your invitation? Request a new link</a>
          <router-link to="/login" class="form__link">Back to login</router-link>
        </div>

        <div v-if="handoffError" class="form-error-summary" role="alert">{{ handoffError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Where an emailed "verify your nursing account" link lands.
 *
 * The page used to complete an invitation end to end: a username and password
 * posted to `/sessionless/updateUsername`, `/sessionless/finalizeRegistration`
 * and `/sessionless/updatePassword`, a Nursys licence check, a licence and
 * insurance re-upload, an `InternalAddNurse` write, and finally flipping the
 * keyon account to Active. The `/sessionless/` half is dead — the bridge
 * intercepts that prefix and maps none of it — and it should not come back: an
 * unauthenticated page that sets a password and activates an account is an
 * account-takeover primitive, which is why the port moved credentials to
 * pacmny-auth. `POST /users/{user}/verification` is deliberately
 * server-to-server, so there is no client-callable replacement either.
 *
 * The rest went with it rather than being kept half-alive. Everything this form
 * wrote about a nurse — licence number and state, insurance, Nursys enrolment,
 * documents — is now an authenticated write on the nursing roster
 * (`PATCH /users/{user}`, `/users/{user}/documents`, the licence endpoints),
 * where the caller's right to make it can actually be checked. A public page
 * writing them on the strength of a nine-digit number in a query string could
 * not.
 *
 * What is left is the hand-off. pacmny-auth blocks the authorization request of
 * anyone carrying `must_change_password` and takes them through choosing one, so
 * "continue to sign in" *is* "finish setting up your account". The button starts
 * the SPA's ordinary OIDC login rather than linking to a page on the provider, so
 * no pacmny-auth URL is hard-coded here.
 */
import store from '@/store';
import oidc from '@/auth/oidcClient';
import { FORGOT_PASSWORD_URL } from '@/api/identityProvider';
import PublicBrandHeader from '@/components/public-layouts/PublicBrandHeader.vue';

export default {
  name: 'verify-nurse-page',
  components: {
    PublicBrandHeader,
  },
  props: {
    accountType: {
      type: String,
      default: '',
    },
    unique: {
      type: String,
      default: '',
    },
    accntnum: {
      type: String,
      default: '',
    },
    // The keyon-era invite e-mail's single-use token (router query `invite`),
    // which authorized that page's public writes. Nothing here writes, and the
    // proof of an invitation is now pacmny-auth's own link, so it is accepted
    // and ignored. Left undeclared, it would fall through onto the root element
    // as an attribute and put the token in the DOM.
    invite: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // A token-free page on the provider: it answers the same way for a known
      // and an unknown address, so linking to it says nothing about who has an
      // account here. The invitation link itself carries a token and can only be
      // minted and mailed by pacmny-auth. It is offered for an invitation that
      // cannot be found, not for an expired one: only the organization sending it
      // again revives an expired invitation (utils/pendingAccountStatus.js).
      forgotPasswordUrl: FORGOT_PASSWORD_URL,
      redirecting: false,
      handoffError: '',
    };
  },
  computed: {
    isLoggedIn() {
      return store.getters['login/isLoggedIn'];
    },
    /** The link's own label, or a sensible one when it carried none. */
    accountTypeLabel() {
      return this.accountType || 'Registered Nurse';
    },
  },
  methods: {
    async continueToSignIn() {
      this.redirecting = true;
      this.handoffError = '';
      try {
        await oidc.beginLogin({ returnTo: '/welcome' });
      } catch (err) {
        // A full-page redirect that never happens leaves the button spinning
        // forever, so an unreachable provider has to be said out loud.
        this.redirecting = false;
        this.handoffError = 'The sign-in service could not be reached. Please try again in a moment.';
        console.error('Could not start sign-in:', err);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../stylesheets/mixins/alignment";
$color-base: #0060ff;
.btn--login {
  color: #fff;
  background-color: $color-base;

  &:hover {
    background-color: lighten($color-base, 5%);
  }
}
.form__link {
  color: white;
  text-decoration: none;
}
#sbmtuserbtn {
  border-radius: 0.375em;
}
.button-group {
  margin-top: 1.5rem;

  .form__link {
    color: $color-base;
    font-size: 0.85rem;
    margin-top: 0.5rem;
    display: block;

    &:hover {
      text-decoration: underline;
    }
  }
}
.content {
  width: 100%;
  height: auto;
  background: url("../../assets/images/bg.jpg") no-repeat center fixed; // TODO image subject to copyright
  background-size: cover;
  position: relative;
}
.reset-wrapper {
  height: 100vh;
  width: 100%;
  position: relative;
}

.reset {
  width: 60% !important;
  background-color: #fff;
  padding: 2rem;
  -moz-box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12),
    0 1px 5px 0 rgba(0, 0, 0, 0.2);

  @include absoluteCenter(380px);

  > h3 {
    text-align: center;
  }
}
.reset__introduction {
  color: darken(gray, 10%);
  font-size: 14px;
  text-align: center;
}
.form-error-summary {
  margin: 0.75rem 0 0;
  padding: 0.9rem 1.15rem;
  background: #fdecea;
  border: 1px solid #f3b4b0;
  border-radius: 6px;
  color: #9b2c2c;
}

@media only screen and (max-width: 768px) {
  .reset {
    width: 95% !important;
  }
}
</style>
